import { Fragment, memo, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Menu } from 'lucide-react';

import { useTranslation } from 'react-i18next';

const HomeIcon = memo(function HomeIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className="h-5 w-5"
    >
      <path
        d="M3 9.8125L12 3.1875L21 9.8125V20.8125H3V9.8125ZM5 10.9375V18.8125H19V10.9375L12 5.8125L5 10.9375ZM10 18.8125V12.8125H14V18.8125H10ZM17.5 8.3125L19.5 9.8125V5.3125H17.5V8.3125Z"
        fill="currentColor"
      />
    </svg>
  );
});

import { HeaderUserProfile } from '@/components/shared/HeaderUserProfile';
import { LanguageSwitcher } from '@/components/shared/LanguageSwitcher';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { PATHS } from '@/constants/path';
import { cn } from '@/lib/utils';

interface BreadcrumbItemData {
  label: string;
  href?: string;
  state?: Record<string, unknown>;
}

const ROUTE_BREADCRUMBS: Record<string, BreadcrumbItemData[]> = {
  [PATHS.ADMIN.USERS.CREATE]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'users.title', href: PATHS.ADMIN.USERS.LIST },
    { label: 'common.addNew' },
  ],
  [PATHS.ADMIN.USERS.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'users.title' },
  ],
  [PATHS.ADMIN.ROLES.CREATE]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'roles.title', href: PATHS.ADMIN.ROLES.LIST },
    { label: 'common.addNew' },
  ],
  [PATHS.ADMIN.ROLES.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'roles.title' },
  ],
  [PATHS.ADMIN.ORGANIZATION.COMPANIES.CREATE]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    {
      label: 'companies.organizationStructure',
      href: PATHS.ADMIN.ORGANIZATION.LIST,
    },
    { label: 'common.addNew' },
  ],
  [PATHS.ADMIN.ORGANIZATION.DEPARTMENTS.CREATE]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    {
      label: 'companies.organizationStructure',
      href: PATHS.ADMIN.ORGANIZATION.LIST,
      state: { activeTab: 'department' },
    },
    { label: 'common.addNew' },
  ],
  [PATHS.ADMIN.ORGANIZATION.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'companies.organizationStructure' },
  ],
  [PATHS.ADMIN.PERMISSIONS.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'header.sidebar.permissions' },
  ],
  [PATHS.ADMIN.REPORTS.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'header.sidebar.reportManagement' },
  ],
  [PATHS.ADMIN.APPS.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'header.sidebar.appManagement' },
  ],
  [PATHS.ADMIN.SETTINGS.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'settings.breadcrumb' },
  ],
  [PATHS.ADMIN.ACTIVITY.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'header.sidebar.activityLog' },
  ],
  [PATHS.ADMIN.ROOT]: [{ label: 'admin.breadcrumb' }],
  [PATHS.ADMIN.PROFILE.ROOT]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'profile.title' },
  ],
  [PATHS.REPORT_VIEW]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'header.sidebar.reportManagement' },
  ],
  [PATHS.ADMIN.REPORT_FILTERS.LIST]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'reportFilters.title' },
  ],
  [PATHS.ADMIN.REPORT_FILTERS.CREATE]: [
    { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
    { label: 'reportFilters.title', href: PATHS.ADMIN.REPORT_FILTERS.LIST },
    { label: 'common.addNew' },
  ],
};

interface AdminHeaderProps {
  className?: string;
  onSidebarToggle?: () => void;
  isMobile?: boolean;
}

export const AdminHeader = memo(function AdminHeader({
  className,
  onSidebarToggle,
  isMobile = false,
}: AdminHeaderProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const breadcrumbs = useMemo(() => {
    const path = location.pathname;

    // Check for exact match first
    if (ROUTE_BREADCRUMBS[path]) {
      return ROUTE_BREADCRUMBS[path];
    }

    // Check for user edit route (dynamic :id)
    if (
      path.match(/^\/admin\/users\/[^/]+$/) &&
      path !== PATHS.ADMIN.USERS.CREATE
    ) {
      return [
        { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
        { label: 'users.title', href: PATHS.ADMIN.USERS.LIST },
        { label: 'users.editTitle' },
      ];
    }

    // Check for role edit route (dynamic :id)
    if (
      path.match(/^\/admin\/roles\/[^/]+$/) &&
      path !== PATHS.ADMIN.ROLES.CREATE
    ) {
      return [
        { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
        { label: 'roles.title', href: PATHS.ADMIN.ROLES.LIST },
        { label: 'roles.detail' },
      ];
    }

    // Check for company edit route (dynamic :id)
    if (
      path.match(/^\/admin\/organization\/companies\/[^/]+$/) &&
      path !== PATHS.ADMIN.ORGANIZATION.COMPANIES.CREATE
    ) {
      return [
        { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
        {
          label: 'companies.organizationStructure',
          href: PATHS.ADMIN.ORGANIZATION.LIST,
        },
        { label: 'companies.edit' },
      ];
    }

    // Check for department edit route (dynamic :id)
    if (
      path.match(/^\/admin\/organization\/departments\/[^/]+$/) &&
      path !== PATHS.ADMIN.ORGANIZATION.DEPARTMENTS.CREATE
    ) {
      return [
        { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
        {
          label: 'companies.organizationStructure',
          href: PATHS.ADMIN.ORGANIZATION.LIST,
          state: { activeTab: 'department' },
        },
        { label: 'departments.edit' },
      ];
    }

    // Check for report filter edit route (dynamic :id)
    if (
      path.match(/^\/admin\/report-filters\/[^/]+$/) &&
      path !== PATHS.ADMIN.REPORT_FILTERS.CREATE
    ) {
      return [
        { label: 'admin.breadcrumb', href: PATHS.ADMIN.ROOT },
        { label: 'reportFilters.title', href: PATHS.ADMIN.REPORT_FILTERS.LIST },
        { label: 'reportFilters.detail' },
      ];
    }

    // Fallback: check startsWith for other routes
    for (const [route, items] of Object.entries(ROUTE_BREADCRUMBS)) {
      if (path.startsWith(route)) {
        return items;
      }
    }
    return [{ label: 'common.home' }];
  }, [location.pathname]);

  return (
    <header
      className={cn(
        'bg-card border-border flex items-center border-b px-3 md:px-6',
        className,
      )}
      style={{ height: '48px' }}
    >
      <div className="flex w-full items-center justify-between gap-2 md:gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
          {isMobile && (
            <button
              type="button"
              onClick={onSidebarToggle}
              className="hover:bg-primary/10 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded transition-colors"
              title="Toggle menu"
            >
              <Menu
                size={20}
                className="text-muted-foreground"
              />
            </button>
          )}

          <Breadcrumb className="min-w-0 flex-1">
            <BreadcrumbList className="flex-nowrap">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to={PATHS.DASHBOARD}
                    className="text-muted-foreground hover:text-primary flex items-center transition-colors"
                  >
                    <HomeIcon />
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbs.length > 0 && <BreadcrumbSeparator />}
              {breadcrumbs.map((item, index) => (
                <Fragment key={index}>
                  {index > 0 && <BreadcrumbSeparator />}
                  <BreadcrumbItem className="truncate">
                    {item.href ? (
                      <BreadcrumbLink asChild>
                        <Link
                          to={item.href}
                          state={item.state}
                          className="truncate"
                        >
                          {t(item.label)}
                        </Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage className="truncate">
                        {t(item.label)}
                      </BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="flex shrink-0 items-center gap-2 md:gap-4">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          <div className="bg-border hidden h-8 w-px sm:block" />
          <HeaderUserProfile />
        </div>
      </div>
    </header>
  );
});

export default AdminHeader;
